import React, { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import apiClient from '../../../../../services/api/apiClient';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import ClientSelect from '../../../../select/ClientSelect';
import { IClient } from '../../../../../utils/types/IClient';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../../others/QueryErrorAlerts';

const typesComptes = ['Agent', 'Client'];
const roles = ['Admin', 'AdminViewer', 'Commercial'];

//#region interfaces

interface NewUserProps {
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const NewUserModalContent: React.FC<NewUserProps> = ({ setShowModal }) => {
    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    //#region useState

    const [typeCompte, setTypeCompte] = useState<string>('Agent');
    const [role, setRole] = useState<string>('Admin');
    const [email, setEmail] = useState<string>();
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);

    const [submittingOperation, setSubmittingOperation] = useState<boolean>(false);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    // ---

    const selectedClient = clients.find((client) => client.cardCode === selectedCardCode) || null;

    // ---

    //#endregion

    //#region refs

    //#endregion

    //#region methods

    const submitForm = async () => {
        let errorMsg: string | null = null;

        if (!['Agent', 'Client'].includes(typeCompte)) {
            errorMsg = 'Type compte invalide.';
        } else if (typeCompte === 'Client' && !selectedCardCode) {
            errorMsg = 'Client incorrect.';
        } else if (typeCompte === 'Agent' && !['Admin', 'AdminViewer', 'Commercial'].includes(role)) {
            errorMsg = 'Rôle incorrect.';
        } else if (!email) {
            errorMsg = 'Adresse e-mail incorrecte.';
        }

        if (errorMsg) {
            Swal.fire({
                text: errorMsg,
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        try {
            setSubmittingOperation(true);

            const response = await apiClient.post('/api/compteUtilisateur/createUser', {
                typeCompte: typeCompte,
                role: role,
                email: email,
                cardCode: selectedCardCode,
            });
            if (response.data.success === true) {
                let confMsg = `
                    <div style="user-select: text;">
                        <p>Utilisateur créé avec succès.</p>
                        ${response.data.data?.message ? `<p>● Message : ${response.data.data.message}</p>` : ''}
                        ${
                            response.data.data?.password
                                ? `
                            <p>
                            ● Mot de passe : 
                            <strong id="password-text">${response.data.data.password}</strong>
                            <button id="copy-btn" type="button" class="btn btn-1" style="text-align: center;">
                                <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-copy"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" /><path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" /></svg> Copier
                            </button>
                            </p>
                        `
                                : ''
                        }
                    </div>
                    `;

                Swal.fire({
                    html: confMsg,
                    icon: 'success',
                    confirmButtonText: 'OK',
                    didOpen: () => {
                        const copyBtn = document.getElementById('copy-btn');
                        const passwordText = document.getElementById('password-text')?.textContent;

                        copyBtn?.addEventListener('click', () => {
                            if (passwordText) {
                                navigator.clipboard.writeText(passwordText).then(() => {
                                    copyBtn.innerHTML = '<svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-copy-check"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path stroke="none" d="M0 0h24v24H0z" /><path d="M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" /><path d="M4.012 16.737a2 2 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" /><path d="M11 14l2 2l4 -4" /></svg> Copié !';
                                    setTimeout(() => {
                                        copyBtn.innerHTML = '<svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-copy"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" /><path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" /></svg> Copier';
                                    }, 2000);
                                });
                            }
                        });
                    },
                }).then(() => {
                    setShowModal?.(false);
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: "Erreur lors l'enregistrement de l'opération. " + response.data.message,
                    },
                ]);
            }
        } catch (err) {
            addAlert('danger', formatAxiosError(err)?.message ?? "Erreur lors de l'ajout du compte utilisateur.");
        } finally {
            setSubmittingOperation(false);
        }
    };

    //#endregion

    //#region useEffect

    useEffect(() => {
        if (typeCompte === 'Agent') {
            setRole('Admin');
            setEmail('');
        } else if (typeCompte === 'Client') {
            setRole('Client');
            setEmail(selectedClient?.eMail ?? '');
        }
    }, [typeCompte, selectedClient]);

    //#endregion

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(() => [{ isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients }], [isClientsError, clientsError, refetchClients]);

    // ---

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
            <QueryErrorAlerts queries={queryErrors} />

            <div>
                <div className="row">
                    <div className="col-md-12 form-group">
                        <label>Type Compte</label>
                        <select
                            value={typeCompte}
                            onChange={(e) => {
                                setTypeCompte(e.target.value);
                            }}
                            className="form-select"
                        >
                            {typesComptes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="hr-text">#</div>

                    {typeCompte === 'Agent' && (
                        <>
                            <div className="col-md-12 form-group">
                                <label>Rôle</label>
                                <select
                                    value={role}
                                    onChange={(e) => {
                                        setRole(e.target.value);
                                    }}
                                    className="form-select"
                                >
                                    {roles.map((role) => (
                                        <option key={role} value={role}>
                                            {role}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    {typeCompte === 'Client' && (
                        <>
                            <div className="col-md-12 form-group">
                                <label>Client</label>
                                <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                            </div>

                            <div className="col-md-12 form-group">
                                <label>Code Client</label>
                                <input type="text" className="form-control" value={selectedCardCode ?? ''} disabled />
                            </div>
                        </>
                    )}

                    <div className="col-md-12 form-group">
                        <label>Adresse e-mail</label>
                        <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                </div>

                <div className="text-end mt-3">
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => {
                            submitForm();
                        }}
                        disabled={submittingOperation}
                    >
                        {submittingOperation ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                &nbsp; Valider
                            </>
                        ) : (
                            `Valider`
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

const NewUserModal: React.FC<NewUserProps> = () => {
    //#region useState

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Functions to toggle the modal

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    //#endregion

    // ---

    return (
        <div>
            <Button className="btn btn-primary btn-5 d-none d-sm-inline-block" onClick={handleOpenModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                </svg>
                Ajouter un utilisateur
            </Button>

            {/* Modal component */}
            <Modal show={showModal} onHide={handleCloseModal} className="fade" centered scrollable backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Nouveau Utilisateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <NewUserModalContent setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default NewUserModal;
